import { useState, useEffect } from 'react';
import {
  LuSearch, LuPackage, LuGift, LuShoppingCart, LuBanknote, LuSmartphone, LuNotebookPen,
  LuMinus, LuPlus, LuTrash2, LuWallet, LuReceipt, LuCheck,
} from 'react-icons/lu';
import { useAuth } from '../../context/AuthContext';
import API, { SERVER_URL } from '../../api/axios';
import AppLayout from '../layout/AppLayout';
import { Page, PageHeader, Card, StatCard, Grid, Badge, Tabs, EmptyState, Loader, Valeur } from '../ui';
import { C, fmt, fmtF } from '../../theme';
import './Caisse.css';

const MODES_PAIEMENT = [
  { id: 'especes',      label: 'Espèces', icon: LuBanknote },
  { id: 'mobile_money', label: 'Mobile',  icon: LuSmartphone },
  { id: 'credit',       label: 'Crédit',  icon: LuNotebookPen },
];
const LIBELLES_MODE = { especes: 'Espèces', mobile_money: 'Mobile Money', credit: 'Crédit' };

const urlImage = chemin => SERVER_URL + '/uploads/produits/' + chemin.replace(/\\/g, '/').split('/').pop();

/** Caisse partagée par les profils serveur et caissier. */
export default function Caisse({ role }) {
  const { utilisateur } = useAuth();
  const [etablissement, setEtablissement] = useState(null);
  const [produits, setProduits] = useState([]);
  const [kits, setKits] = useState([]);
  const [panier, setPanier] = useState([]);
  const [recherche, setRecherche] = useState('');
  const [ongletProduit, setOngletProduit] = useState('produits');
  const [modePaiement, setModePaiement] = useState('especes');
  const [chargement, setChargement] = useState(true);
  const [message, setMessage] = useState('');
  const [onglet, setOnglet] = useState('caisse');
  const [ventes, setVentes] = useState([]);
  const [chargementVente, setChargementVente] = useState(false);

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { chargerDonnees(); }, []);

  const chargerDonnees = async () => {
    try {
      const etabRes = await API.get('/etablissements/' + utilisateur?.etablissement_id);
      setEtablissement(etabRes.data.etablissement);
      const [produitsRes, kitsRes, ventesRes] = await Promise.all([
        API.get('/produits'),
        API.get('/kits'),
        API.get('/ventes'),
      ]);
      setProduits(produitsRes.data.produits || []);
      setKits(kitsRes.data.kits || []);
      setVentes(ventesRes.data.ventes || []);
    } catch (err) {
      console.error(err);
    } finally {
      setChargement(false);
    }
  };

  const showMessage = (msg) => {
    setMessage(msg);
    setTimeout(() => setMessage(''), 3000);
  };

  const ajouterAuPanier = (item, type) => {
    const existe = panier.find(p => p.id === item.id && p.type === type);
    if (existe) {
      modifierQuantite(item.id, type, 1);
    } else {
      setPanier([...panier, {
        id: item.id, type,
        nom: item.nom,
        prix: parseFloat(type === 'produit' ? item.prix : item.prix_kit),
        quantite: 1,
        stock: type === 'produit' ? item.quantite : 999,
        image: item.image_principale || null,
        unite: item.unite || ''
      }]);
    }
  };

  const modifierQuantite = (id, type, delta) => {
    setPanier(panier.map(p => {
      if (p.id === id && p.type === type) {
        const nq = p.quantite + delta;
        if (nq <= 0) return null;
        if (nq > p.stock) {
          showMessage('⚠️ Stock insuffisant pour "' + p.nom + '"');
          return p;
        }
        return { ...p, quantite: nq };
      }
      return p;
    }).filter(Boolean));
  };

  const total = panier.reduce((s, p) => s + parseFloat(p.prix) * p.quantite, 0);
  const nbArticles = panier.reduce((s, p) => s + p.quantite, 0);

  const validerVente = async () => {
    if (panier.length === 0) {
      showMessage('⚠️ Le panier est vide !');
      return;
    }
    setChargementVente(true);
    try {
      const produits_v = panier.filter(p => p.type === 'produit').map(p => ({ produit_id: p.id, quantite: p.quantite }));
      const kits_v = panier.filter(p => p.type === 'kit').map(p => ({ kit_id: p.id, quantite: p.quantite }));
      await API.post('/ventes', {
        etablissement_id: utilisateur.etablissement_id,
        mode_paiement: modePaiement,
        produits: produits_v,
        kits: kits_v,
      });
      showMessage('✅ Vente enregistrée avec succès !');
      setPanier([]);
      chargerDonnees();
    } catch (err) {
      showMessage('❌ ' + (err.response?.data?.message || 'Erreur lors de la vente'));
    } finally {
      setChargementVente(false);
    }
  };

  // Stats aujourd'hui
  const aujourdhui = new Date().toDateString();
  const ventesDuJour = ventes.filter(v => new Date(v.created_at).toDateString() === aujourdhui);
  const ventesJour = ventesDuJour.filter(v => v.statut === 'validee');
  const caJour = ventesJour.reduce((t, v) => t + parseFloat(v.montant_total || 0), 0);
  const especesJour = ventesJour.filter(v => v.mode_paiement === 'especes').reduce((t, v) => t + parseFloat(v.montant_total || 0), 0);
  const mobileJour = ventesJour.filter(v => v.mode_paiement === 'mobile_money').reduce((t, v) => t + parseFloat(v.montant_total || 0), 0);

  const produitsFiltres = produits.filter(p =>
    p.nom.toLowerCase().includes(recherche.toLowerCase()) ||
    (p.code_barre && p.code_barre.includes(recherche))
  );

  const kitsFiltres = kits.filter(k =>
    k.nom.toLowerCase().includes(recherche.toLowerCase()) && k.actif
  );

  if (chargement) return <Loader />;

  const tonMessage = message.startsWith('✅') ? 'success' : message.startsWith('⚠️') ? 'warn' : 'danger';
  const couleursMessage = {
    success: [C.successLight, '#047857'],
    warn:    [C.warnLight,    '#B45309'],
    danger:  [C.dangerLight,  '#B91C1C'],
  }[tonMessage];

  return (
    <AppLayout role={role} activeId={onglet} onSelect={setOnglet} subtitle={etablissement?.nom}>
      {message && (
        <div role="status" style={{
          position: 'fixed', top: 16, right: 16, zIndex: 1100, maxWidth: 'calc(100vw - 32px)',
          padding: '12px 18px', borderRadius: 12, fontWeight: 600, fontSize: 14,
          boxShadow: '0 8px 24px rgba(15,23,42,0.15)', animation: 'sp-fade-up .2s ease both',
          backgroundColor: couleursMessage[0], color: couleursMessage[1],
        }}>{message.replace(/^(✅|❌|⚠️)\s*/, '')}</div>
      )}

      {/* ── CAISSE ── */}
      {onglet === 'caisse' && (
        <div className="cs-layout">

          {/* GAUCHE — CATALOGUE */}
          <div className="cs-left">
            {/* Résumé du jour */}
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, marginBottom: 16 }}>
              <Chip label="Ventes du jour" value={ventesJour.length} />
              <Chip label="Revenus du jour" value={`${fmtF(caJour)}`} strong />
              <Chip icon={LuBanknote} label="Espèces" value={`${fmtF(especesJour)}`} />
              <Chip icon={LuSmartphone} label="Mobile" value={`${fmtF(mobileJour)}`} />
            </div>

            <div style={{ position: 'relative', marginBottom: 14 }}>
              <LuSearch aria-hidden style={{ position: 'absolute', left: 14, top: '50%', transform: 'translateY(-50%)', color: C.textMuted, fontSize: 18 }} />
              <input
                type="search"
                aria-label="Rechercher un produit"
                placeholder="Rechercher un produit ou scanner un code-barres…"
                value={recherche}
                onChange={(e) => setRecherche(e.target.value)}
                autoFocus
                style={{ width: '100%', minHeight: 48, padding: '0 16px 0 44px', borderRadius: 12, border: `1px solid ${C.border}`, fontSize: 15, backgroundColor: C.surface }}
              />
            </div>

            <Tabs
              tabs={[
                { id: 'produits', label: `Produits (${produitsFiltres.length})`, icon: LuPackage },
                { id: 'kits',     label: `Kits (${kitsFiltres.length})`,         icon: LuGift },
              ]}
              value={ongletProduit}
              onChange={setOngletProduit}
            />

            {ongletProduit === 'produits' && (
              produitsFiltres.length === 0
                ? <EmptyState icon={LuPackage} title="Aucun produit trouvé" text={recherche && 'Essayez un autre mot-clé.'} />
                : (
                  <div className="cs-grid">
                    {produitsFiltres.map(p => {
                      const rupture = p.quantite === 0;
                      const bas = !rupture && p.quantite <= p.seuil_alerte;
                      const dansPanier = panier.find(x => x.id === p.id && x.type === 'produit');
                      return (
                        <button key={p.id} className="cs-product" disabled={rupture} onClick={() => ajouterAuPanier(p, 'produit')} style={{ position: 'relative' }}>
                          {dansPanier && <QteBulle n={dansPanier.quantite} />}
                          {p.image_principale
                            ? <img className="cs-thumb" src={urlImage(p.image_principale)} alt="" />
                            : <div className="cs-thumb"><LuPackage aria-hidden /></div>}
                          <div style={{ fontWeight: 600, fontSize: 14, lineHeight: 1.3 }}>{p.nom}</div>
                          <div style={{ fontWeight: 800, fontSize: 15, color: 'var(--accent)', fontVariantNumeric: 'tabular-nums' }}>{fmtF(p.prix)}</div>
                          <Badge tone={rupture ? 'danger' : bas ? 'warn' : 'neutral'}>
                            {rupture ? 'Rupture' : `${p.quantite} ${p.unite}`}
                          </Badge>
                        </button>
                      );
                    })}
                  </div>
                )
            )}

            {ongletProduit === 'kits' && (
              kitsFiltres.length === 0
                ? <EmptyState icon={LuGift} title="Aucun kit trouvé" />
                : (
                  <div className="cs-grid">
                    {kitsFiltres.map(kit => {
                      const dansPanier = panier.find(x => x.id === kit.id && x.type === 'kit');
                      return (
                        <button key={kit.id} className="cs-product" onClick={() => ajouterAuPanier(kit, 'kit')} style={{ position: 'relative' }}>
                          {dansPanier && <QteBulle n={dansPanier.quantite} />}
                          <div className="cs-thumb"><LuGift aria-hidden /></div>
                          <div style={{ fontWeight: 600, fontSize: 14, lineHeight: 1.3 }}>{kit.nom}</div>
                          <div style={{ fontWeight: 800, fontSize: 15, color: 'var(--accent)', fontVariantNumeric: 'tabular-nums' }}>{fmtF(kit.prix_kit)}</div>
                          <Badge>{(kit.produits || []).length} produit(s)</Badge>
                        </button>
                      );
                    })}
                  </div>
                )
            )}
          </div>

          {/* DROITE — PANIER */}
          <aside className="cs-right" aria-label="Panier">
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '18px 20px', borderBottom: `1px solid ${C.border}` }}>
              <h2 style={{ margin: 0, fontSize: 16, fontWeight: 700, display: 'flex', alignItems: 'center', gap: 8 }}>
                <LuShoppingCart aria-hidden /> Panier
                {nbArticles > 0 && <Badge tone="info">{nbArticles}</Badge>}
              </h2>
              {panier.length > 0 && (
                <button onClick={() => setPanier([])} style={{ display: 'inline-flex', alignItems: 'center', gap: 6, border: 'none', background: 'none', color: C.danger, fontWeight: 600, fontSize: 13, padding: '6px 4px' }}>
                  <LuTrash2 aria-hidden /> Vider
                </button>
              )}
            </div>

            {panier.length === 0 ? (
              <div style={{ flex: 1, display: 'grid', placeItems: 'center', padding: 24 }}>
                <EmptyState icon={LuShoppingCart} title="Panier vide" text="Touchez un produit pour l'ajouter." />
              </div>
            ) : (
              <div style={{ flex: 1, overflowY: 'auto', padding: '4px 20px' }}>
                {panier.map(item => (
                  <div key={item.type + item.id} style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '12px 0', borderBottom: `1px solid ${C.separator}` }}>
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ fontWeight: 600, fontSize: 14, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{item.nom}</div>
                      <div style={{ color: C.textMuted, fontSize: 12 }}>{fmtF(item.prix)}/unité</div>
                    </div>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                      <button className="cs-qty" aria-label={`Retirer un ${item.nom}`} onClick={() => modifierQuantite(item.id, item.type, -1)}><LuMinus /></button>
                      <span style={{ minWidth: 22, textAlign: 'center', fontWeight: 700, fontVariantNumeric: 'tabular-nums' }}>{item.quantite}</span>
                      <button className="cs-qty" aria-label={`Ajouter un ${item.nom}`} onClick={() => modifierQuantite(item.id, item.type, 1)}><LuPlus /></button>
                    </div>
                    <div style={{ fontWeight: 700, fontSize: 14, minWidth: 72, textAlign: 'right', fontVariantNumeric: 'tabular-nums' }}>
                      {fmtF(item.prix * item.quantite)}
                    </div>
                  </div>
                ))}
              </div>
            )}

            {/* Paiement */}
            <div style={{ padding: '16px 20px 20px', borderTop: `1px solid ${C.border}`, backgroundColor: '#F8FAFC' }}>
              <div style={{ fontSize: 12, fontWeight: 600, color: C.textMuted, marginBottom: 8 }}>Mode de paiement</div>
              <div role="radiogroup" aria-label="Mode de paiement" style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 6, marginBottom: 16 }}>
                {MODES_PAIEMENT.map(mode => {
                  const actif = modePaiement === mode.id;
                  const Icon = mode.icon;
                  return (
                    <button
                      key={mode.id}
                      role="radio"
                      aria-checked={actif}
                      onClick={() => setModePaiement(mode.id)}
                      style={{
                        display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 4, minHeight: 56,
                        padding: '8px 4px', borderRadius: 10, fontSize: 12, fontWeight: 600,
                        border: `1.5px solid ${actif ? 'var(--accent)' : C.border}`,
                        backgroundColor: actif ? 'color-mix(in srgb, var(--accent) 10%, white)' : C.surface,
                        color: actif ? 'var(--accent)' : C.textSub,
                      }}
                    >
                      <Icon aria-hidden style={{ fontSize: 18 }} />{mode.label}
                    </button>
                  );
                })}
              </div>

              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', marginBottom: 12 }}>
                <span style={{ fontWeight: 600, color: C.textSub }}>Total</span>
                <span style={{ fontSize: 26, fontWeight: 800, color: C.text, fontVariantNumeric: 'tabular-nums', letterSpacing: '-0.02em' }}>{fmt(total)} <small style={{ fontSize: 14, fontWeight: 600, color: C.textMuted }}>FCFA</small></span>
              </div>

              <button
                onClick={validerVente}
                disabled={chargementVente || panier.length === 0}
                style={{
                  width: '100%', minHeight: 52, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8,
                  border: 'none', borderRadius: 12, fontWeight: 700, fontSize: 15, color: '#fff',
                  backgroundColor: C.success,
                }}
              >
                {chargementVente
                  ? <><span style={{ width: 16, height: 16, border: '2px solid rgba(255,255,255,.5)', borderTopColor: '#fff', borderRadius: '50%', animation: 'sp-spin .8s linear infinite' }} /> Enregistrement…</>
                  : <><LuCheck aria-hidden /> Valider la vente</>}
              </button>
            </div>
          </aside>
        </div>
      )}

      {/* ── MES VENTES ── */}
      {onglet === 'ventes' && (
        <Page>
          <PageHeader title="Mes ventes du jour" subtitle={new Date().toLocaleDateString('fr-FR', { weekday: 'long', day: 'numeric', month: 'long' })} />

          <Grid min={200} style={{ marginBottom: 20 }}>
            <StatCard icon={LuWallet}       label="Revenus du jour" value={`${fmtF(caJour)}`}      color={C.success} />
            <StatCard icon={LuShoppingCart} label="Ventes validées" value={ventesJour.length}       color={C.info} />
            <StatCard icon={LuBanknote}     label="Espèces"        value={`${fmtF(especesJour)}`} color={C.warn} />
            <StatCard icon={LuSmartphone}   label="Mobile Money"   value={`${fmtF(mobileJour)}`}  color={C.purple} />
          </Grid>

          <Card style={{ padding: 0, overflow: 'hidden' }}>
            {ventesDuJour.length === 0 ? (
              <EmptyState icon={LuReceipt} title="Aucune vente aujourd'hui" text="Vos ventes apparaîtront ici dès la première validée." />
            ) : (
              <div style={{ overflowX: 'auto' }}>
                <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 14, minWidth: 520 }}>
                  <thead>
                    <tr style={{ backgroundColor: '#F8FAFC' }}>
                      {['Facture', 'Heure', 'Paiement', 'Montant', 'Statut'].map((h, i) => (
                        <th key={h} style={{ padding: '10px 16px', textAlign: i === 3 ? 'right' : 'left', color: C.textMuted, fontWeight: 600, fontSize: 12, textTransform: 'uppercase', letterSpacing: '0.04em' }}>{h}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {ventesDuJour.map(v => (
                      <tr key={v.id} style={{ borderTop: `1px solid ${C.border}` }}>
                        <td style={{ ...td, fontFamily: 'ui-monospace, monospace', fontWeight: 600, fontSize: 13 }}>{v.numero_facture || '#' + v.id}</td>
                        <td style={{ ...td, color: C.textSub }}>{new Date(v.created_at).toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' })}</td>
                        <td style={{ ...td, color: C.textSub }}>{LIBELLES_MODE[v.mode_paiement] || v.mode_paiement}</td>
                        <td style={{ ...td, textAlign: 'right', fontWeight: 700 }}>{fmtF(v.montant_total)}</td>
                        <td style={td}>
                          <Badge tone={v.statut === 'validee' ? 'success' : 'danger'}>{v.statut === 'validee' ? 'Validée' : 'Annulée'}</Badge>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </Card>
        </Page>
      )}
    </AppLayout>
  );
}

function Chip({ icon: Icon, label, value, strong }) {
  return (
    <div style={{ display: 'inline-flex', alignItems: 'center', gap: 8, padding: '6px 12px', borderRadius: 999, backgroundColor: C.surface, border: `1px solid ${C.border}`, fontSize: 13 }}>
      {Icon && <Icon aria-hidden style={{ color: C.textMuted }} />}
      <span style={{ color: C.textMuted }}>{label}</span>
      <strong style={{ color: strong ? 'var(--accent)' : C.text, fontVariantNumeric: 'tabular-nums' }}><Valeur value={value} /></strong>
    </div>
  );
}

function QteBulle({ n }) {
  return (
    <span aria-label={`${n} dans le panier`} style={{
      position: 'absolute', top: 8, right: 8, minWidth: 24, height: 24, padding: '0 6px', borderRadius: 12,
      display: 'grid', placeItems: 'center', fontSize: 12, fontWeight: 700, color: '#fff', backgroundColor: 'var(--accent)',
    }}>{n}</span>
  );
}

const td = { padding: '12px 16px', color: C.text };
